/**
 * Dashboard charts.
 *
 * Colours come from CSS custom properties (--serie-1/2, --grelha, …) defined in
 * index.css, so light and dark are two selected sets rather than an automatic
 * flip. The pair was validated with the dataviz validator: all six checks pass
 * in both modes (worst adjacent CVD ΔE 24.7 light / 26.8 dark).
 */

import { useEffect, useState, type ReactNode } from 'react';
import {
  Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import { dataCurta, horas, mesPorExtenso, numero } from '../formatos';

/** Recharts needs real values, not CSS vars, so read them off the document. */
function useCoresGrafico() {
  const ler = () => {
    const estilo = getComputedStyle(document.documentElement);
    const obter = (nome: string, alternativa: string) =>
      estilo.getPropertyValue(nome).trim() || alternativa;
    return {
      serie1: obter('--serie-1', '#2a78d6'),
      serie2: obter('--serie-2', '#eb6834'),
      grelha: obter('--grelha', '#e6e9ee'),
      texto: obter('--texto-secundario', '#6b7683'),
      superficie: obter('--superficie', '#ffffff'),
    };
  };

  const [cores, setCores] = useState(ler);

  useEffect(() => {
    const observador = new MutationObserver(() => setCores(ler()));
    observador.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const aoMudar = () => setCores(ler());
    media.addEventListener('change', aoMudar);
    return () => { observador.disconnect(); media.removeEventListener('change', aoMudar); };
  }, []);

  return cores;
}

function CaixaTooltip({ titulo, linhas }: { titulo: string; linhas: { rotulo: string; valor: string; cor?: string }[] }) {
  return (
    <div className="rounded-lg border border-papel-200 bg-white px-3 py-2 text-xs shadow-lg
                    dark:border-tinta-700 dark:bg-tinta-800">
      <p className="mb-1 font-medium text-tinta-900 dark:text-papel-100">{titulo}</p>
      {linhas.map((linha) => (
        <p key={linha.rotulo} className="flex items-center gap-1.5 text-tinta-600 dark:text-papel-300">
          {linha.cor && (
            <span className="h-2 w-2 rounded-sm" style={{ background: linha.cor }} aria-hidden />
          )}
          <span>{linha.rotulo}:</span>
          <span className="font-medium tabular-nums text-tinta-900 dark:text-papel-100">{linha.valor}</span>
        </p>
      ))}
    </div>
  );
}

export function Painel({ titulo, descricao, children, acao }: {
  titulo: string; descricao?: string; children: ReactNode; acao?: ReactNode;
}) {
  return (
    <section className="cartao p-5">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-tinta-900 dark:text-papel-100">{titulo}</h2>
          {descricao && (
            <p className="mt-0.5 text-xs text-tinta-500 dark:text-tinta-400">{descricao}</p>
          )}
        </div>
        {acao}
      </div>
      {children}
    </section>
  );
}

/**
 * Hours per day. Two series — normal and overtime — so overtime is visible as
 * its own block rather than hidden inside a single total.
 */
export function GraficoHorasPorDia({ dados }: {
  dados: { data: string; total_horas: number; n_funcionarios: number }[];
}) {
  const cores = useCoresGrafico();

  if (!dados.length) {
    return <p className="py-12 text-center text-sm text-tinta-500 dark:text-tinta-400">Sem dados neste mês.</p>;
  }

  const formatados = dados.map((d) => ({ ...d, rotulo: dataCurta(d.data) }));

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={formatados} margin={{ top: 4, right: 8, bottom: 4, left: -16 }}>
          <CartesianGrid stroke={cores.grelha} vertical={false} />
          <XAxis
            dataKey="rotulo" tickLine={false} axisLine={{ stroke: cores.grelha }}
            tick={{ fill: cores.texto, fontSize: 11 }} interval="preserveStartEnd"
          />
          <YAxis
            tickLine={false} axisLine={false} tick={{ fill: cores.texto, fontSize: 11 }}
            width={48}
          />
          <Tooltip
            cursor={{ fill: cores.grelha, fillOpacity: 0.4 }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const item = payload[0].payload as typeof formatados[number];
              return (
                <CaixaTooltip
                  titulo={dataCurta(item.data, true)}
                  linhas={[
                    { rotulo: 'Horas', valor: horas(item.total_horas), cor: cores.serie1 },
                    { rotulo: 'Funcionários', valor: numero(item.n_funcionarios) },
                  ]}
                />
              );
            }}
          />
          {/* 4px rounded data-end, anchored to the baseline. */}
          <Bar dataKey="total_horas" fill={cores.serie1} radius={[4, 4, 0, 0]} maxBarSize={28} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Twelve-month trend. One series, so the title names it and no legend is needed. */
export function GraficoEvolucao({ dados }: {
  dados: { mes: string; total_horas: number; n_servicos: number }[];
}) {
  const cores = useCoresGrafico();

  if (dados.length < 2) {
    return (
      <p className="py-12 text-center text-sm text-tinta-500 dark:text-tinta-400">
        A tendência aparece assim que houver dois meses de registos.
      </p>
    );
  }

  const formatados = dados.map((d) => ({ ...d, rotulo: d.mes.slice(5) + '/' + d.mes.slice(2, 4) }));

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={formatados} margin={{ top: 8, right: 12, bottom: 4, left: -16 }}>
          <CartesianGrid stroke={cores.grelha} vertical={false} />
          <XAxis
            dataKey="rotulo" tickLine={false} axisLine={{ stroke: cores.grelha }}
            tick={{ fill: cores.texto, fontSize: 11 }}
          />
          <YAxis tickLine={false} axisLine={false} tick={{ fill: cores.texto, fontSize: 11 }} width={48} />
          <Tooltip
            cursor={{ stroke: cores.grelha, strokeWidth: 1 }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const item = payload[0].payload as typeof formatados[number];
              return (
                <CaixaTooltip
                  titulo={mesPorExtenso(item.mes)}
                  linhas={[
                    { rotulo: 'Horas', valor: horas(item.total_horas), cor: cores.serie1 },
                    { rotulo: 'Serviços', valor: numero(item.n_servicos) },
                  ]}
                />
              );
            }}
          />
          <Line
            type="monotone" dataKey="total_horas" stroke={cores.serie1} strokeWidth={2}
            dot={{ r: 4, fill: cores.serie1, stroke: cores.superficie, strokeWidth: 2 }}
            activeDot={{ r: 6, fill: cores.serie1, stroke: cores.superficie, strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Horizontal magnitude bars for the "by employee" and "by location" panels.
 * A plain table with a bar behind each row beats a pie: it stays readable past
 * five categories and the numbers are right there.
 */
export function BarrasProporcao({ itens }: {
  itens: { rotulo: string; valor: number; secundario?: string }[];
}) {
  if (!itens.length) {
    return <p className="py-8 text-center text-sm text-tinta-500 dark:text-tinta-400">Sem dados.</p>;
  }
  const maximo = Math.max(...itens.map((i) => i.valor), 1);

  return (
    <ul className="space-y-2.5">
      {itens.map((item) => (
        <li key={item.rotulo}>
          <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
            <span className="truncate text-tinta-700 dark:text-papel-300">{item.rotulo}</span>
            <span className="shrink-0 tabular-nums font-medium text-tinta-900 dark:text-papel-100">
              {horas(item.valor)}
              {item.secundario && (
                <span className="ml-2 font-normal text-tinta-500 dark:text-tinta-400">{item.secundario}</span>
              )}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-papel-200 dark:bg-tinta-700">
            <div
              className="h-full rounded-full"
              style={{ width: `${(item.valor / maximo) * 100}%`, background: 'var(--serie-1)' }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
